import { useState } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import HighlightedText from './partials/HighlightedText';
import InputText from './partials/InputText';
import { SlCheckbox } from '@shoelace-style/shoelace/dist/react';
import { subscribe } from './utils/Mailchimp';

const fields = [
    'first_name',
    'last_name',
    'email',
    'subscribe',
];

export default function Register() {
    const [submitted, setSubmitted] = useState(false);

    if (submitted) {
        return (
            <div className="min-h-svh flex flex-col bg-calm">
                <div className="flex flex-grow items-center justify-center" id="full">
                    <div className="text-center">
                        <h1 className="text-3xl font-bold leading-10 text-dusk mb-[60px]">
                            <HighlightedText text="Welcome"/> to the team!
                        </h1>
                        <p className="text-lg leading-6 text-dusk">
                            Check your emails for the next steps
                        </p>
                        <p className="text-lg leading-6 text-dusk">
                            and how to refer and earn 💰
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-svh flex flex-col bg-calm">
            <div className="flex flex-grow items-center justify-center" id="full">
                <RegisterForm setSubmitted={setSubmitted} />
            </div>

            <MobileGradient />
        </div>
    );
}

const MobileGradient = () => {
    return (
        <div className="fixed md:hidden bottom-0 w-full h-[100px] bg-gradient-to-t from-calm/100 from-10% via-calm/60 via-50% to-calm/0 to-90%"></div>
    );
}

const RegisterForm = ({setSubmitted}) => {
    const [responses, setResponses] = useState({});
    const [firstSubmit, setFirstSubmit] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
        setFocus,
        reset,          // Reset method to populate form data
        trigger,        // Trigger method to force validation
        control
    } = useForm({
        mode: 'onChange', // Makes the form validation real-time
    });

    const handleUpdate = (data) => {
        if (errors[data.id]) return;

        setResponses({
            ...responses,
            [data.id]: data.value,
        });
        
        trigger(data.id);
    }

    const validate = () => {
        // get all field names to force validate all fields
        const fieldNames = fields.map((q) => q.id);
        trigger(fieldNames.join(','));
    }

    const onSubmit: SubmitHandler<any> = async (data) => {
        data['affiliate'] = true;

        // make a unique 8 char alphanumeric hash for the user
        const hash = Math.random().toString(36).substring(2, 10);
        data['utm_code'] = hash;

        const url = `/sheets/sheet.php`;
        
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });
            
            if (response.ok) {
                console.log('Data successfully sent to Google Sheets');
            } else {
                console.log('Error sending data', response  );
            }
        } catch (error) {
            console.error('Request failed:', error);
        }

        if (data.email) {
            const subscribeData = {
                email: data.email,
                subscribe: data.subscribe,
                type: 'affiliate',
                hash: hash,
                first_name: data.first_name,
                last_name: data.last_name,
            }

            try {
                const response = await subscribe(subscribeData);
                console.log('Mailchimp response:', response);
            } catch (error) {
                console.error('Failed to subscribe to Mailchimp:', error);
                // Optionally handle the error, e.g., notify the user
            }
        }

        setSubmitted(true);
    };

    return (
        <form 
            onSubmit={ handleSubmit(onSubmit) }
            className="w-full mt-[40px] md:mt-[85px] pb-0 md:!pb-4 mb-20 md:w-[765px] bg-calm px-[15px] md:px-10 mx-auto "
        >
            <div className="">
                <div className="font-semibold text-base leading-snug mb-2.5">
                    First name
                </div>
                <InputText
                    type="text"
                    q={{ id: 'first_name', label: 'First Name', validation: { required: 'required' } }}
                    register={ register } 
                    errors={ errors } 
                    addResponse={ handleUpdate }
                    response={ responses.first_name || '' } 
                    firstSubmit={ firstSubmit }
                    control={ control }
                    validate={ validate }
                />
            </div>

            <div className="mt-2.5">
                <div className="font-semibold text-base leading-snug mb-2.5">
                    Last name
                </div>
                <InputText
                    type="text"
                    q={{ id: 'last_name', label: 'Last Name', validation: { required: 'required' } }}
                    register={ register } 
                    errors={ errors } 
                    addResponse={ handleUpdate }
                    response={ responses.last_name || '' } 
                    firstSubmit={ firstSubmit }
                    control={ control }
                    validate={ validate }
                />
            </div>

            <div className="mt-2.5">
                <div className="font-semibold text-base leading-snug mb-2.5">
                    Email
                </div>
                <InputText
                    type="email"
                    q={{ 
                        id: 'email', 
                        label: 'Email', 
                        validation: { 
                            required: 'required', 
                            pattern: {
                                value: "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
                                message: "Invalid email address",
                            } 
                        } 
                    }}
                    register={ register } 
                    errors={ errors } 
                    addResponse={ handleUpdate }
                    response={ responses.email || '' } 
                    firstSubmit={ firstSubmit }
                    control={ control }
                    validate={ validate }
                />
            </div>

            <div className="mt-[27px] checky">
                <Controller
                    control={control}
                    name="subscribe"
                    defaultValue={false}
                    render={({ field: { onChange, value } }) => (
                        <SlCheckbox
                            checked={value}
                            onSlChange={(e) => onChange(e.target.checked)}
                            size="large"
                            style={{
                                '--sl-input-border-width': "1px", 
                                '--sl-input-border-color': "#EFECEB"
                            }}
                        >
                        <span className="ml-0.5 text-base font-normal leading-tight font-[Montserrat]">
                            <span className="font-bold mr-2">
                            Get the latest!
                            </span>
                            Sign up for our Knowledge is Power newsletter ✨
                        </span>
                        </SlCheckbox>
                    )}
                />
            </div>

            <div className="flex w-full justify-center">
                <button
                    type="submit"
                    className="mt-10 w-[140px] bg-lava rounded-full uppercase flex justify-center text-white hover:bg-white hover:!text-dusk transition-all duration-300"
                >
                    <div 
                        className="grid content-center mx-5 h-[33px] my-2 text-base font-medium leading-snug"
                    >
                        SUBMIT
                    </div>
                </button>
            </div>
        </form>
    )
}